import React, { useMemo, useContext, useState } from 'react';
import { graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { ModalTransition } from '@atlaskit/modal-dialog';

import type {
	ConfluenceFeatures,
	SupportedEditions,
} from '@atlassian/confluence-end-user-upgrade-request-modal';

import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';

import type {
	useEnableEndUserRequestUpgradeQuery$data,
	useEnableEndUserRequestUpgradeQuery$variables,
} from './__generated__/useEnableEndUserRequestUpgradeQuery.graphql';
import { EndUserRequestUpgradeModalEntrypoint } from './EndUserRequestUpgradeModal/EndUserRequestUpgradeModalEntrypoint.ts';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

export type TouchpointInfo = {
	requestedEdition: SupportedEditions;
	requestedFeature: ConfluenceFeatures;
};

export const ELIGIBLE_TOUCHPOINTS: Record<string, TouchpointInfo> = {
	pageInsights: {
		requestedEdition: 'standard',
		requestedFeature: 'pageInsights',
	},
	archiveUpsellDialog: {
		requestedEdition: 'standard',
		requestedFeature: 'archive',
	},
	generalAccessLevelSelector: {
		requestedEdition: 'standard',
		requestedFeature: 'pagePermissions',
	},
	shareAndRestrictDialogUpsell: {
		requestedEdition: 'standard',
		requestedFeature: 'pagePermissions',
	},
	quickSummaryUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'rovo',
	},
	teamCalendarUpsellScreen: {
		requestedEdition: 'premium',
		requestedFeature: 'calendars',
	},
	'whiteboardCreationPaywall-upfrontDialog': {
		requestedEdition: 'premium',
		requestedFeature: 'whiteboards',
	},
};

export type EligibleTouchpoints = keyof typeof ELIGIBLE_TOUCHPOINTS;

export const useEnableEndUserRequestUpgrade = (source: string) => {
	const { cloudId, edition, userId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const offeringName = ELIGIBLE_TOUCHPOINTS[source]?.requestedEdition ?? 'standard';
	const { loading, data } = useQueryInterop<
		useEnableEndUserRequestUpgradeQuery$data,
		useEnableEndUserRequestUpgradeQuery$variables
	>(
		graphql`
			query useEnableEndUserRequestUpgradeQuery($cloudId: ID!, $offeringName: String!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlementId
						entitlement {
							id
							__typename
							experienceCapabilities {
								changeOfferingV2(offeringName: $offeringName) {
									isAvailableToUser
									experienceUrl
								}
							}

							transactionAccount {
								key
								isBillToPresent
							}

							relatesFromEntitlements {
								entitlementId
								relationshipType
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				cloudId,
				offeringName,
			},
			fetchPolicy: 'cache-first',
			skip:
				!fg('connie_end_user_request_kill_switch') ||
				isSiteAdmin ||
				edition !== ConfluenceEdition.FREE ||
				!(source in ELIGIBLE_TOUCHPOINTS),
			client: getAGGClient(),
		},
	);

	const isEligibleForEndUserRequest = useMemo(() => {
		const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
		const isInCollection = entitlement?.relatesFromEntitlements?.find(
			(item) => item?.relationshipType === 'COLLECTION',
		)?.entitlementId;
		const isChangeOfferingAvailable =
			entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser;
		const changeOfferingToStandardExperienceUrl =
			entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl;
		const canUpgrade = Boolean(isChangeOfferingAvailable && changeOfferingToStandardExperienceUrl);
		const hasPaymentDetailsEntered = Boolean(entitlement?.transactionAccount?.isBillToPresent);
		const entitlementInfo = data?.tenantContexts?.[0]?.entitlementInfo;
		const entitlementId = entitlementInfo?.entitlementId;
		const transactionAccountId = entitlement?.transactionAccount?.key;

		return Boolean(
			!loading &&
				!isInCollection &&
				edition === ConfluenceEdition.FREE &&
				hasPaymentDetailsEntered &&
				!canUpgrade &&
				Boolean(userId) &&
				Boolean(changeOfferingToStandardExperienceUrl) &&
				Boolean(transactionAccountId) &&
				Boolean(entitlementId),
		);
	}, [data, loading, edition, userId]);

	return {
		showRequestUpgradeButton:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			isEligibleForEndUserRequest &&
			fg('connie_end_user_request_kill_switch') &&
			expValEquals('connie_end_user_request_exp', 'cohort', 'variation'),
		showRequestUpgradeButtonAATest:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			isEligibleForEndUserRequest &&
			fg('connie_end_user_request_kill_switch') &&
			expVal('connie_end_user_request_exp_aa', 'cohort', 'not-enrolled') !== 'not-enrolled',
		loading,
	};
};

export const EndUserRequestUpgradeButton = ({
	source,
}: {
	source: string;
}): React.JSX.Element | null => {
	const [showModal, setShowModal] = useState(false);
	const [isLoadingModal, setIsLoadingModal] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleCloseModal = () => setShowModal(false);
	const handleOpenModal = () => {
		setIsLoadingModal(true);
		setShowModal(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'endUserRequestUpgradeButton',
				source: source,
			},
		}).fire();
	};

	return (
		<>
			<Button onClick={handleOpenModal} appearance="primary" isLoading={isLoadingModal}>
				<FormattedMessage
					id="experiment-confluence-enable-end-user-request.request-upgrade-button.label"
					defaultMessage="Request to try"
					description="Label text for the request upgrade button in the confluence enable end user request experiment"
				/>
			</Button>
			<ModalTransition>
				{showModal && (
					<EndUserRequestUpgradeModalEntrypoint
						source={source}
						handleCloseModal={handleCloseModal}
						setIsLoadingModal={setIsLoadingModal}
					/>
				)}
			</ModalTransition>
		</>
	);
};
